import moment from 'moment';
import {
    calculatePmt,
    calculateIof,
    calculateCommission,
    calculateCet,
    adjustForGracePeriod,
    calculateGuaranteeConcession,
} from 'bizcap-commons-web/dist/loan';
import { BizOs } from 'bizos-commons-loan-simulator';

import { ISimulationResult, ISimulationParams } from 'interfaces/interfaces';
import { LoanApplication, DiscountType } from 'generated/graphql';
import { calculateComissionDiscount } from './discount-utils';
import { CONFIG } from 'constants/constants';
import { convertToSimulationPaymentInterval } from './utils';

const defaultPaymentDay = moment()
    .startOf('day')
    .add(5, 'd')
    .add(1, 'M')
    .toDate();
const defaultLoanDay = moment().startOf('day').toDate();
const insurancePremiumMonthlyRate = 0.002;

export const recalculateSimulationResult = (
    loanApplication: LoanApplication,
    discounts: DiscountType[],
    insurance?: boolean,
    productBizId?: string
): ISimulationResult =>
    calculateSimulation(
        {
            amount: loanApplication?.acceptedProposal?.amount,
            tenure: loanApplication?.acceptedProposal?.tenure,
            interestRate: loanApplication?.acceptedProposal?.interestRate,
            paymentDay: loanApplication?.acceptedProposal?.paymentDay,
            simplesNacional: loanApplication?.borrower?.simplesNacional,
            comission: loanApplication?.acceptedProposal?.fixedComission
                ? loanApplication.acceptedProposal?.commission
                : undefined,
            hasRenegotiation: loanApplication?.hasRenegotiation,
            productBizId,
            partnerPaysFees:
                loanApplication.product?.mdcSettings?.partnerPaysFees,
            downPaymentAmount:
                loanApplication?.acceptedProposal?.downPaymentAmount,
            amountRequested: loanApplication?.amountRequested,
            useInsurance: insurance,
            paymentInterval: convertToSimulationPaymentInterval(
                loanApplication?.acceptedProposal?.paymentInterval
            ),
            downPayments: loanApplication?.acceptedProposal?.downPayments,
            guaranteeConcessionFee:
                loanApplication?.acceptedProposal?.guaranteeConcessionFee ?? 0,
        },
        discounts
    );

export const calculateSimulation = (
    simulation: ISimulationParams,
    discounts: DiscountType[]
): ISimulationResult => {
    const simulationResult = {} as ISimulationResult;

    const guaranteeConcessionAmount = simulation.guaranteeConcessionFee
        ? calculateGuaranteeConcession({
              amount: simulation.amount,
              tenure: simulation.tenure,
              guaranteeConcessionFee: simulation.guaranteeConcessionFee,
          })
        : 0;

    if (simulation.comission) {
        simulationResult.originalComission = comission(simulation);
        simulationResult.finalComission = simulation.comission;
    } else {
        simulationResult.finalComission = comission(simulation);
    }

    if (discounts && discounts.length > 0) {
        simulationResult.originalComission = simulationResult.finalComission;

        let calculatedComission = simulationResult.finalComission;

        discounts.forEach((discount) => {
            calculatedComission = calculateComissionDiscount(
                discount,
                calculatedComission,
                simulation.hasRenegotiation
            );
        });

        simulationResult.finalComission = calculatedComission;
    }

    const amountWithFees =
        simulation.amount +
        simulationResult.finalComission +
        guaranteeConcessionAmount;

    simulationResult.iof = iof({
        ...simulation,
        amount: amountWithFees,
    });

    simulationResult.insurancePremium = 0;
    simulationResult.insurancePmt = 0;

    if (simulation.useInsurance) {
        const insurancePremiumRate =
            insurancePremiumMonthlyRate * simulation.tenure;
        const principalAmountConsideringInsurance = Math.round(
            amountWithFees /
                (1 -
                    simulationResult.iof / amountWithFees -
                    insurancePremiumRate)
        );

        const iofConsideringInsurance =
            (simulationResult.iof * principalAmountConsideringInsurance) /
            amountWithFees;
        const insurancePremium =
            principalAmountConsideringInsurance * insurancePremiumRate;

        simulationResult.iof = Math.round(iofConsideringInsurance);
        simulationResult.insurancePremium = Math.round(insurancePremium);
        simulationResult.insurancePmt = insurancePremium / simulation.tenure;
    }

    simulationResult.adjustedAmount = adjustedAmountForGracePeriod(
        {
            ...simulation,
            amount: amountWithFees + simulationResult.insurancePremium,
        },
        simulationResult.iof
    );

    if (simulation.productBizId === 'capital-giro') {
        const bizOsSimulation = BizOs.Commons.LoanSimulator.Wasm.simulate({
            principal:
                simulation.amount +
                simulationResult.finalComission +
                simulationResult.iof +
                simulationResult.insurancePremium,
            term: simulation.tenure,
            date: defaultLoanDay,
            monthlyInterestRate: simulation.interestRate / 100,
            firstPaymentDate: simulation.paymentDay,
        });

        simulationResult.pmt = bizOsSimulation.payment;
    } else {
        const pmtInfo = calculatePmt({
            amount: simulationResult.adjustedAmount,
            tenure: simulation.tenure,
            interestRate: simulation.interestRate,
            paymentInterval: simulation.paymentInterval,
            downPayments: simulation.downPayments,
        });

        simulationResult.pmt = Math.round(pmtInfo.pmt * 100) / 100;
        simulationResult.downPaymentPmt = pmtInfo.downPaymentsPmt;
    }

    if (simulation.partnerPaysFees) {
        simulationResult.pmt =
            (simulation.amountRequested - simulation.downPaymentAmount) /
            simulation.tenure;
    }

    simulationResult.downPaymentsTotal =
        simulation.downPayments && simulation.downPayments > 0
            ? simulationResult.downPaymentPmt * simulation.downPayments
            : 0;

    simulationResult.cet = cet(simulation, simulationResult.pmt);
    simulationResult.total =
        simulationResult.pmt * simulation.tenure +
        simulationResult.downPaymentsTotal;
    simulationResult.interestAmount =
        simulation.interestRate > 0
            ? simulationResult.total -
              simulation.amount -
              simulationResult.finalComission -
              simulationResult.iof -
              simulationResult.insurancePremium
            : 0;
    simulationResult.feesAmount = simulationResult.total - simulation.amount;

    if (simulation.guaranteeConcessionFee) {
        simulationResult.guaranteeConcessionAmount = guaranteeConcessionAmount;
    }

    return simulationResult;
};

export const comission = (simulation: ISimulationParams) => {
    if (CONFIG.COMISSION.FIXED_VALUE()) {
        return 0;
    }

    if (CONFIG.COMISSION.FIXED_PERCENT()) {
        return (
            Math.round(simulation.amount * CONFIG.COMISSION.FIXED_PERCENT()) /
            100
        );
    }

    return (
        Math.round(
            calculateCommission({
                amount: simulation.amount,
                tenure: simulation.tenure,
                paymentInterval: simulation.paymentInterval,
            }) * 100
        ) / 100
    );
};

const iof = (simulation: ISimulationParams) => {
    return (
        Math.round(
            calculateIof({
                amount: simulation.amount,
                tenure: simulation.tenure,
                loanDate: defaultLoanDay,
                dueDate: simulation.paymentDay
                    ? moment(simulation.paymentDay).toDate()
                    : defaultPaymentDay,
                isSimplesNacional: simulation.simplesNacional,
                paymentInterval: simulation.paymentInterval,
                downPayments: simulation.downPayments,
            }) * 100
        ) / 100
    );
};

const adjustedAmountForGracePeriod = (
    simulation: ISimulationParams,
    calculatedIof: number
) => {
    return (
        Math.round(
            adjustForGracePeriod({
                amount: simulation.amount,
                loanDate: defaultLoanDay,
                dueDate: simulation.paymentDay
                    ? moment(simulation.paymentDay).toDate()
                    : defaultPaymentDay,
                interestRate: simulation.interestRate,
                iof: calculatedIof,
                paymentInterval: simulation.paymentInterval,
            }) * 100
        ) / 100
    );
};

const cet = (simulation: ISimulationParams, calculatedPmt: number) => {
    return calculateCet({
        amount: simulation.amount,
        tenure: simulation.tenure,
        interestRate: simulation.interestRate,
        loanDate: defaultLoanDay,
        dueDate: simulation.paymentDay
            ? moment(simulation.paymentDay).toDate()
            : defaultPaymentDay,
        pmt: calculatedPmt,
        paymentInterval: simulation.paymentInterval,
        downPayments: simulation.downPayments,
    });
};
